/* eslint-disable complexity */
/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypeOptions, BenefitTypes } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { NumberInputFormik, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import dayjs from 'dayjs';
import { type FormikErrors } from 'formik';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useEffect, useMemo } from 'react';
import type * as Yup from 'yup';

import { DEFAULT_PHARMACY_PLACE_OF_SERVICE } from 'services/constants';
import { useGetUnitsOfMeasureQuery } from 'store/api/intakeCaseApi';
import {
    useGetDiagnosesQuery,
    useGetParentSuppliersQuery,
    useGetPlacesOfServiceQuery,
    useGetProductCodesQuery,
    useGetProductConfigForPayerQuery,
    useGetProductsForSegmentQuery,
} from 'store/api/referenceApi';
import {
    type FormSections,
    type IntakeFormSectionSchemas,
} from 'store/intakeForm/intakeFormSchema';

type Section = typeof FormSections.PRODUCT;
type ProductFormValues = Yup.InferType<(typeof IntakeFormSectionSchemas)[Section]>;

interface ProductInputsProps {
    segmentId?: string;
    disabled?: boolean;
    benefitRef?: ComponentProps<typeof SuggestSingleSelectFormik>['inputRef'];
}

interface ApiUom {
    unitCode?: string;
    abbreviation?: string | null;
    displayName?: string | null;
}
interface UomOption {
    name: string;
    value: string;
}

interface ProductCode {
    name: string;
    strength?: string | number | null;
    strength_unit_of_measure?: string | null;
    dosage?: string | null;
    quantity_unit_of_measure_code?: string | null;
}

type MedicationDetailFieldValue = string | number | null | undefined;

const hasMedicationDetailValue = (value: MedicationDetailFieldValue): boolean => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== '';
};

const toOptions = (uoms?: ApiUom[]): UomOption[] =>
    (uoms ?? [])
        .map((u) => {
            const name = (u.abbreviation?.trim() ? u.abbreviation : u.displayName)?.trim();
            const value = u.unitCode?.trim();
            return name && value ? { name, value } : null;
        })
        .filter((x): x is UomOption => !!x);

export const ProductInputs = ({
    segmentId = undefined,
    disabled = false,
    benefitRef = undefined,
}: ProductInputsProps) => {
    const formik = useFormikContext<ProductFormValues>();
    const { showLighthouseIntakeCaseEpaUiUpdates } = useFlags<{
        showLighthouseIntakeCaseEpaUiUpdates: boolean;
    }>();

    const { data: { quantityUom, strengthUom } = {} } = useGetUnitsOfMeasureQuery();
    const { data: placesOfService, isFetching: placesOfServiceFetching } =
        useGetPlacesOfServiceQuery();
    const { data: suppliers, isFetching: suppliersFetching } = useGetParentSuppliersQuery();
    const { data: products, isFetching: productsFetching } = useGetProductsForSegmentQuery(
        segmentId ? { segmentId } : skipToken,
    );

    const { data: diagnoses, isFetching: diagnosesFetching } = useGetDiagnosesQuery();
    const diagnosesCodeOptions = useMemo(
        () =>
            (diagnoses ?? [])
                .map((diagnosis) =>
                    diagnosis.codes.map((code) => ({
                        name: code.description,
                        secondaryData: code.code,
                        oid: diagnosis.oid,
                    })),
                )
                .flat()
                .sort((a, b) => sortAsc(a.name, b.name)),
        [diagnoses],
    );

    const { data: productConfig, isFetching: productConfigFetching } =
        useGetProductConfigForPayerQuery(
            segmentId && formik.values.productServiceOid && formik.values.benefit
                ? {
                      segmentId,
                      productServiceOid: formik.values.productServiceOid,
                      benefit: formik.values.benefit,
                  }
                : skipToken,
        );

    const { data: productCodes, isFetching: productCodesFetching } = useGetProductCodesQuery(
        formik.values.productServiceOid
            ? { productServiceOid: formik.values.productServiceOid }
            : skipToken,
    );
    const strengthUomValues = useMemo(
        () => toOptions(strengthUom as ApiUom[] | undefined),
        [strengthUom],
    );
    const quantityUomValues = useMemo(
        () => toOptions(quantityUom as ApiUom[] | undefined),
        [quantityUom],
    );

    const productCodeList = productCodes as ProductCode[] | undefined;
    const selectedNdcItem = useMemo(() => {
        const current = formik.values.productServiceId;
        return current ? { name: current } : undefined;
    }, [formik.values.productServiceId]);

    const selectedStrengthUomItem = useMemo(() => {
        const label = (formik.values.strengthUnitOfMeasure ?? '').toString().trim();
        if (!label) return undefined;

        const match = strengthUomValues.find((v) => v.name.trim() === label);
        return match ?? { name: label, value: label }; // allow showing existing label even if not in list
    }, [formik.values.strengthUnitOfMeasure, strengthUomValues]);

    const selectedQuantityUomItem = useMemo(() => {
        const code = (formik.values.quantityUnitOfMeasure ?? '').toString().trim();
        if (!code) return undefined;

        const match = quantityUomValues.find((v) => v.value === code);
        return match;
    }, [formik.values.quantityUnitOfMeasure, quantityUomValues]);

    const { setFieldValue } = formik;

    useEffect(() => {
        (async () => {
            if (!productCodeList?.length) return;

            if (!formik.values.productServiceId) {
                const match = productCodeList.find(
                    (c) => c.name === formik.values.productServiceId,
                );
                const ndc = match ?? productCodeList[0];

                const updates: Promise<void | FormikErrors<ProductFormValues>>[] = [
                    setFieldValue('productServiceId', ndc.name),
                ];
                if (!hasMedicationDetailValue(formik.values.strength)) {
                    updates.push(setFieldValue('strength', ndc.strength));
                }
                if (!hasMedicationDetailValue(formik.values.strengthUnitOfMeasure)) {
                    updates.push(
                        setFieldValue(
                            'strengthUnitOfMeasure',
                            ndc.strength_unit_of_measure?.trim() ?? '',
                        ),
                    );
                }
                if (!hasMedicationDetailValue(formik.values.quantityUnitOfMeasure)) {
                    updates.push(
                        setFieldValue(
                            'quantityUnitOfMeasure',
                            ndc.quantity_unit_of_measure_code?.trim() ?? '',
                        ),
                    );
                }

                await Promise.all(updates);
            }
        })();
    }, [productCodeList, setFieldValue, formik]);

    return (
        <div className="sds_intake-form-grid">
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="benefit"
                        label="Benefit"
                        required
                        disabled={disabled}
                        values={BenefitTypeOptions}
                        inputRef={benefitRef}
                        onChange={(newVal) => {
                            (async () =>
                                Promise.all([
                                    setFieldValue('benefit', newVal?.value),
                                    ...(newVal?.value === BenefitTypes.PHARMACY
                                        ? [
                                              setFieldValue(
                                                  'placeOfService',
                                                  DEFAULT_PHARMACY_PLACE_OF_SERVICE,
                                              ),
                                          ]
                                        : []),
                                    setFieldValue('rxSig', undefined),
                                    setFieldValue('additionalMedications', []),
                                ]))();
                        }}
                        selectedItem={BenefitTypeOptions.find(
                            ({ value }) => value === formik.values.benefit,
                        )}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="placeOfService"
                        label="Place Of Service"
                        values={placesOfService}
                        isLoading={placesOfServiceFetching}
                        disabled={disabled || formik.values.benefit === BenefitTypes.PHARMACY}
                        onChange={(newVal) => {
                            (async () => setFieldValue('placeOfService', newVal?.code))();
                        }}
                        selectedItem={placesOfService?.find(
                            ({ code }) => code === formik.values.placeOfService,
                        )}
                    />
                </div>
            </div>
            {formik.values.benefit === BenefitTypes.MEDICAL && (
                <div className="sds_intake-form-section__row">
                    <div style={{ gridColumn: '1', minWidth: 0 }}>
                        <SuggestSingleSelectFormik
                            id="preferredPharmacyNpi"
                            label="Preferred Servicing Pharmacy"
                            disabled={disabled}
                            values={suppliers}
                            isLoading={suppliersFetching}
                            onChange={(newVal) => {
                                (async () => setFieldValue('preferredPharmacyNpi', newVal?.npi))();
                            }}
                            selectedItem={suppliers?.find(
                                ({ npi }) => npi === formik.values.preferredPharmacyNpi,
                            )}
                        />
                    </div>
                </div>
            )}
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="productServiceOid"
                        label="Product"
                        required
                        disabled={disabled}
                        values={products}
                        isLoading={productsFetching}
                        onChange={(newVal) => {
                            (async () =>
                                Promise.all([
                                    setFieldValue('productServiceOid', newVal?.productServiceOid),
                                    setFieldValue('isBrand', newVal?.isBrandDefault),
                                    setFieldValue(
                                        'productServiceDescription',
                                        newVal?.isBrandDefault
                                            ? newVal.brandProductDescription
                                            : newVal?.genericProductDescription,
                                    ),
                                ]))();
                        }}
                        selectedItem={products?.find(
                            ({ productServiceOid }) =>
                                productServiceOid === formik.values.productServiceOid,
                        )}
                    />
                </div>
            </div>
            {showLighthouseIntakeCaseEpaUiUpdates && !!formik.values.productServiceOid && (
                <div className="sds_intake-form-section__row">
                    <div style={{ gridColumn: '1', minWidth: 0 }}>
                        <SuggestSingleSelectFormik
                            id="productServiceId"
                            label="NDC"
                            disabled={disabled}
                            values={productCodes}
                            required
                            isLoading={productCodesFetching}
                            onChange={(newVal) => {
                                const ndc = (newVal as ProductCode | null) ?? null;
                                (async () =>
                                    Promise.all([
                                        setFieldValue('productServiceId', ndc?.name ?? undefined),
                                        setFieldValue('strength', ndc?.strength),
                                        setFieldValue(
                                            'strengthUnitOfMeasure',
                                            ndc?.strength_unit_of_measure?.trim() ?? '',
                                        ),
                                        setFieldValue(
                                            'quantityUnitOfMeasure',
                                            ndc?.quantity_unit_of_measure_code?.trim() ?? '',
                                        ),
                                    ]))();
                            }}
                            selectedItem={selectedNdcItem}
                            inputCopyButton
                        />
                    </div>
                </div>
            )}
            {(() => {
                const selectedProduct = products?.find(
                    ({ productServiceOid }) =>
                        productServiceOid === formik.values.productServiceOid,
                );
                return (
                    selectedProduct?.brandProductDescription !==
                        selectedProduct?.genericProductDescription && (
                        <div className="sds_intake-form-section__row">
                            <div style={{ gridColumn: '1', minWidth: 0 }}>
                                <RadioButtonGroupFormik
                                    name="isBrand"
                                    label="Does The Patient Require Brand?"
                                    required
                                    disabled={disabled}
                                    options={['No', 'Yes']}
                                    selectedValue={formik.values.isBrand ? 'Yes' : 'No'}
                                    onChange={(newVal) => {
                                        (async () => {
                                            const isBrand = newVal === 'Yes';
                                            const product = products?.find(
                                                ({ productServiceOid }) =>
                                                    productServiceOid ===
                                                    formik.values.productServiceOid,
                                            );
                                            await Promise.all([
                                                setFieldValue('isBrand', isBrand),
                                                setFieldValue(
                                                    'productServiceDescription',
                                                    isBrand
                                                        ? product?.brandProductDescription
                                                        : product?.genericProductDescription,
                                                ),
                                            ]);
                                        })();
                                    }}
                                />
                            </div>
                        </div>
                    )
                );
            })()}
            {formik.values.benefit === BenefitTypes.PHARMACY && (
                <>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1', minWidth: 0 }}>
                            <TextInputFormik id="strength" label="Strength" disabled={disabled} />
                        </div>
                        <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                            <SuggestSingleSelectFormik
                                id="strengthUnitOfMeasure"
                                label="Strength Unit Of Measure"
                                values={strengthUomValues}
                                disabled={disabled}
                                placeholderText="Select"
                                onChange={(newValue) => {
                                    (() =>
                                        formik.setFieldValue(
                                            'strengthUnitOfMeasure',
                                            newValue?.name ?? '',
                                        ))();
                                }}
                                selectedItem={selectedStrengthUomItem}
                                disableAutoPopulate
                            />
                        </div>
                    </div>
                    <div className="sds_intake-form-section__row">
                        <div style={{ gridColumn: '1', minWidth: 0 }}>
                            <NumberInputFormik
                                id="quantity"
                                label="Quantity"
                                disabled={disabled}
                                step="0.001"
                                maxDecimalPlaces={3}
                            />
                        </div>
                        <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                            <SuggestSingleSelectFormik
                                id="quantityUnitOfMeasure"
                                label="Quantity Unit Of Measure"
                                values={quantityUomValues}
                                disabled={disabled}
                                placeholderText="Select"
                                onChange={(newValue) => {
                                    (() =>
                                        formik.setFieldValue(
                                            'quantityUnitOfMeasure',
                                            newValue?.value ?? '',
                                        ))();
                                }}
                                selectedItem={selectedQuantityUomItem}
                                disableAutoPopulate
                            />
                        </div>
                    </div>
                </>
            )}
            {formik.values.benefit === BenefitTypes.MEDICAL && (
                <div className="sds_intake-form-section__row">
                    <div style={{ gridColumn: '1', minWidth: 0 }}>
                        <NumberInputFormik
                            id="productUnits"
                            label="Product Units"
                            disabled={disabled}
                            step="1"
                            maxDecimalPlaces={0}
                        />
                    </div>
                    <div style={{ gridColumn: '2 / -1', minWidth: 0 }}>
                        <SuggestSingleSelectFormik
                            id="productUom"
                            label="Product Units Of Measure"
                            disabled={disabled}
                            isLoading={productConfigFetching}
                            values={productConfig?.uomOptions}
                        />
                    </div>
                </div>
            )}
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <NumberInputFormik
                        id="daysSupply"
                        label="Days Supply"
                        disabled={disabled}
                        step="1"
                        maxDecimalPlaces={0}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <NumberInputFormik
                        id="refills"
                        label="Refills"
                        disabled={disabled}
                        step="1"
                        maxDecimalPlaces={0}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="rxSig"
                        label="Directions"
                        disabled={disabled}
                        isLoading={productConfigFetching}
                        values={productConfig?.directionOptions}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="diagnosisCode"
                        label="Diagnosis"
                        isLoading={diagnosesFetching}
                        values={diagnosesCodeOptions}
                        disabled={disabled}
                        onChange={(newValue) => {
                            (async () => {
                                await setFieldValue('diagnosisCode', newValue?.secondaryData);
                                await setFieldValue('diagnosisOid', newValue?.oid);
                            })();
                        }}
                        selectedItem={diagnosesCodeOptions.find(
                            ({ secondaryData }) => secondaryData === formik.values.diagnosisCode,
                        )}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <DateInputFormik
                        id="needsByDate"
                        label="Needs By Date"
                        disabled={disabled}
                        minDate={dayjs().startOf('day').toDate()}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <CheckboxListFormik
                        name="buyAndBill"
                        label="Buy And Bill?"
                        disabled={disabled}
                        options={['Buy and Bill']}
                        values={formik.values.buyAndBill ? ['Buy and Bill'] : []}
                        onChange={(values) => {
                            (async () => setFieldValue('buyAndBill', values.length === 1))();
                        }}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestSingleSelectFormik
                        id="sentToPharmacyNpi"
                        label="Previous Servicing Pharmacy"
                        disabled={disabled}
                        values={suppliers}
                        isLoading={suppliersFetching}
                        onChange={(newVal) => {
                            (async () => setFieldValue('sentToPharmacyNpi', newVal?.npi))();
                        }}
                        selectedItem={suppliers?.find(
                            ({ npi }) => npi === formik.values.sentToPharmacyNpi,
                        )}
                    />
                </div>
            </div>
            <div className="sds_intake-form-section__row">
                <div style={{ gridColumn: '1', minWidth: 0 }}>
                    <SuggestMultiSelectFormik
                        id="additionalMedications"
                        label="Additional Medications"
                        disabled={disabled}
                        isLoading={productConfigFetching}
                        values={productConfig?.additionalMedOptions}
                    />
                </div>
            </div>
        </div>
    );
};
